import React, { useEffect, useState } from 'react';
import { FileText, FileSpreadsheet, Settings, Layers } from 'lucide-react';

// Contexts
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider, useApp } from './context/AppContext';

// Layout
import Layout from './components/layout/Layout';

// Views
import LoginView from './views/Login/LoginView';
import InicioView from './views/Inicio/InicioView';

// Common Components
import { PlaceholderView } from './components/common';

import ClientesView from './components/modules/Clientes/ClientesView';
import ProveedoresView from './components/modules/Proveedores/ProveedoresView';
import ArticulosView from './components/modules/Articulos/ArticulosView';
import OrdenesEntregaView from './components/modules/OrdenesEntrega/OrdenesEntregaView';
import EmpresasView from './components/modules/Empresas/EmpresasView';
import SolicitudesView from './components/modules/Servicios/SolicitudesView';
import SolicitudesGestionView from './components/modules/Solicitudes/SolicitudesGestionView';
import SolicitudPublicaForm from './components/public/SolicitudPublicaForm';
import CotizacionesView from './components/modules/Cotizaciones/CotizacionesView';
import FacturasView from './components/modules/Factura/FacturasView';
import MultiServiciosView from './components/modules/MultiServicios/MultiServiciosView';
import ReportesView from './components/modules/Reportes/ReportesView';
import ParametrosFiscalesView from './components/modules/ParametrosFiscales/ParametrosFiscalesView';
import FacturasCompraView from './components/modules/Compras/FacturasCompraView';
import ConciliacionBancariaView from './components/modules/ConciliacionBancaria/ConciliacionBancariaView';
import MaquinasView from './components/modules/Maquinas/MaquinasView';
import UsuariosView from './components/modules/Usuarios/UsuariosView';
import PuntoVView from './components/modules/PuntoV/PuntoVView';
import ChatsView from './components/modules/Chats/ChatsView';
import SimcuvView from './components/modules/Simcuv/SimcuvView';

// Styles
import './styles/App.css';

/**
 * Componente principal de la aplicación
 */
const AppContent = () => {
  const { isLoggedIn, loading } = useAuth();
  const { currentView } = useApp();
  const [isPublicRoute, setIsPublicRoute] = useState(false);

  // Detectar si estamos en una ruta pública
  useEffect(() => {
    const path = window.location.pathname;
    setIsPublicRoute(path === '/solicitud-publica');
  }, []);

  // Mostrar loader mientras verifica la sesión
  if (loading && !isPublicRoute) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Si es ruta pública, mostrar formulario sin layout ni autenticación
  if (isPublicRoute) {
    return <SolicitudPublicaForm />;
  }

  // Si no está logueado, mostrar login
  if (!isLoggedIn) {
    return <LoginView />;
  }

  // Renderizar vista según currentView
  const renderView = () => {
    switch (currentView) {
      case 'inicio':
        return <InicioView />;
      
      case 'solicitudes':
        return <SolicitudesView title="Servicios" icon={FileText} />;
      
      case 'solicitudes-gestion':
        return <SolicitudesGestionView title="Solicitudes" icon={FileText} />;
      
      case 'solicitudes-form':
        return <PlaceholderView title="Nueva Solicitud" icon={FileText} />;
      
      case 'cotizaciones':
        return <CotizacionesView title="Cotizaciones" icon={FileText} />;
      
      case 'facturas':
        return <FacturasView />;
      
      case 'proveedores':
        return <ProveedoresView title="Proveedores" icon={Settings} />;

      case 'facturas-compra':
        return <FacturasCompraView />;

      case 'multi-servicios':
        return <MultiServiciosView />;

      case 'reportes':
        return <ReportesView />;

      case 'conciliacion-bancaria':
        return <ConciliacionBancariaView />;

      case 'punto-v':
        return <PuntoVView />;

      case 'chats':
        return <ChatsView />;

      case 'simcuv':
        return <SimcuvView />;

      case 'maquinas':
        return <MaquinasView />;

      case 'parametros-fiscales':
        return <ParametrosFiscalesView />;

      case 'clientes':
        return <ClientesView title="Clientes" icon={Settings} />;
      
      case 'articulos':
        return <ArticulosView title="Artículos" icon={Settings} />;

      case 'ordenes-entrega':
        return <OrdenesEntregaView title="Órdenes de Entrega" icon={Settings} />;

      case 'empresas':
        return <EmpresasView title="Empresas" icon={Settings} />;
      
      case 'usuarios':
        return <UsuariosView />;
      
      default:
        return <InicioView />;
    }
  };

  return (
    <Layout>
      {renderView()}
    </Layout>
  );
};

/**
 * Componente App con Providers
 */
const App = () => {
  return (
    <AuthProvider>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </AuthProvider>
  );
};

export default App;
